/*
 * Generative score for the landing page's opening, synthesised live with the Web Audio API
 * (no audio files, nothing to license):
 * - a warm pad walking I–vi–IV–ii–V–I in D major, one chord per chapter, crossfading (never
 *   sliding) as you travel, over a sub on the root
 * - soft glass notes from the current chord drifting through a ping-pong echo into a long
 *   hall, more often while you scroll; hovering a world plays its note
 * - filtered air, and a pad that opens up as the scroll speeds
 * - a sweep, a low boom and the world's chord for the dive
 * A gentle compressor glues the mix. It fades out (and the context sleeps) while the stage is
 * off screen or the tab is hidden. Browsers allow audio only after a click, so it is created
 * on the sound toggle.
 */

const LEVEL = 0.75; // master gain while audible
const PAD_CUTOFF = 1300; // Hz; opens by up to PAD_OPEN with scroll speed
const PAD_OPEN = 2200;
const AIR = 0.01;

// Core, Salesforce, ERP, consultancy, AI agents, worlds (MIDI notes); neighbours share most tones
const CHORDS = [
  { root: 38, pad: [57, 61, 64, 66, 69] }, // Dmaj9
  { root: 35, pad: [57, 62, 64, 66, 69] }, // Bm11
  { root: 43, pad: [57, 59, 62, 66, 69] }, // Gmaj9
  { root: 40, pad: [59, 62, 66, 67, 71] }, // Em9
  { root: 45, pad: [59, 62, 64, 67, 69] }, // A9sus4
  { root: 38, pad: [57, 61, 64, 66, 69] } // Dmaj9, home again
];
const PANS = [-0.55, 0.35, -0.2, 0.5, -0.4];

const midi = note => 440 * 2 ** ((note - 69) / 12);
// Older Safari's AudioContext methods may not return promises
const quietly = result => result?.catch?.(() => {});
const clampChapter = index => Math.max(0, Math.min(CHORDS.length - 1, Math.round(index) || 0));
// The glass notes: the chord's own tones, one and two octaves up
const sparkle = chord => [...chord.pad.map(n => n + 12), ...chord.pad.map(n => n + 24)].filter(n => n >= 69 && n <= 93);

const noiseBuffer = (ctx, seconds, channels = 1) => {
  const buffer = ctx.createBuffer(channels, Math.floor(ctx.sampleRate * seconds), ctx.sampleRate);
  for (let c = 0; c < channels; c += 1) {
    const data = buffer.getChannelData(c);
    for (let i = 0; i < data.length; i += 1) data[i] = Math.random() * 2 - 1;
  }
  return buffer;
};

// A hall without a sample file: stereo noise that decays and darkens, after a short pre-delay
const hall = (ctx, seconds) => {
  const rate = ctx.sampleRate;
  const length = Math.floor(rate * seconds);
  const pre = Math.floor(rate * 0.025);
  const buffer = ctx.createBuffer(2, length, rate);
  for (let c = 0; c < 2; c += 1) {
    const data = buffer.getChannelData(c);
    let low = 0;
    for (let i = pre; i < length; i += 1) {
      const t = (i - pre) / (length - pre);
      low += (0.9 - 0.8 * t) * (Math.random() * 2 - 1 - low);
      data[i] = low * (1 - t) ** 2 * Math.exp(-2.2 * t);
    }
  }
  return buffer;
};

// A soft, string-like tone: the fundamental with quickly falling overtones
const warmWave = ctx => {
  const overtones = [0, 1, 0.45, 0.22, 0.14, 0.07, 0.05, 0.03, 0.02];
  return ctx.createPeriodicWave(new Float32Array(overtones.length), Float32Array.from(overtones));
};

export const createAmbience = (chapter = 0) => {
  const AudioContext = window.AudioContext || window.webkitAudioContext;
  if (!AudioContext) return null;
  const ctx = new AudioContext();
  const now = () => ctx.currentTime;
  // Ease a parameter toward a value from wherever it is now
  const glide = (param, value, tau, at = now()) => {
    param.cancelScheduledValues(at);
    param.setValueAtTime(param.value, at);
    param.setTargetAtTime(value, at, tau);
  };

  // Mix: dry + hall → softened top → glue compressor → fade → out
  const master = ctx.createGain();
  master.gain.value = 0;
  const glue = ctx.createDynamicsCompressor();
  glue.threshold.value = -14;
  glue.knee.value = 10;
  glue.ratio.value = 2.5;
  glue.attack.value = 0.03;
  glue.release.value = 0.4;
  const soften = ctx.createBiquadFilter();
  soften.type = 'highshelf';
  soften.frequency.value = 6500;
  soften.gain.value = -4;
  const bus = ctx.createGain();
  bus.connect(soften).connect(glue).connect(master).connect(ctx.destination);
  const dry = ctx.createGain();
  dry.gain.value = 0.7;
  dry.connect(bus);
  const reverb = ctx.createConvolver();
  reverb.buffer = hall(ctx, 4.5);
  const wet = ctx.createGain();
  wet.gain.value = 0.5;
  reverb.connect(wet).connect(bus);

  // Ping-pong echo: left, then right, then round again, darker each pass
  const echo = ctx.createGain();
  const left = ctx.createDelay(1);
  const right = ctx.createDelay(1);
  left.delayTime.value = 0.39;
  right.delayTime.value = 0.39;
  const darken = ctx.createBiquadFilter();
  darken.type = 'lowpass';
  darken.frequency.value = 2600;
  const feedback = ctx.createGain();
  feedback.gain.value = 0.36;
  const sides = ctx.createChannelMerger(2);
  echo.connect(left).connect(right).connect(darken).connect(feedback).connect(left);
  left.connect(sides, 0, 0);
  right.connect(sides, 0, 1);
  const echoOut = ctx.createGain();
  echoOut.gain.value = 0.5;
  sides.connect(echoOut);
  echoOut.connect(dry);
  echoOut.connect(reverb);

  // Glass notes go out partly dry, more through the echo and the hall
  const glass = ctx.createGain();
  glass.connect(dry);
  const glassEcho = ctx.createGain();
  glassEcho.gain.value = 0.55;
  glass.connect(glassEcho).connect(echo);
  const glassHall = ctx.createGain();
  glassHall.gain.value = 0.85;
  glass.connect(glassHall).connect(reverb);

  // One low-pass for every chord of the pad
  const padTone = ctx.createBiquadFilter();
  padTone.type = 'lowpass';
  padTone.frequency.value = PAD_CUTOFF;
  padTone.Q.value = 0.6;
  padTone.connect(dry);
  padTone.connect(reverb);
  const wave = warmWave(ctx);

  // Air: decorrelated stereo noise through a wandering band-pass
  const airTone = ctx.createBiquadFilter();
  airTone.type = 'bandpass';
  airTone.frequency.value = 2600;
  airTone.Q.value = 0.6;
  const airLevel = ctx.createGain();
  airLevel.gain.value = AIR;
  const air = ctx.createBufferSource();
  air.buffer = noiseBuffer(ctx, 4, 2);
  air.loop = true;
  air.connect(airTone).connect(airLevel);
  airLevel.connect(dry);
  airLevel.connect(reverb);
  air.start();
  const sweepNoise = noiseBuffer(ctx, 1.6);

  // Slow drifts: the pad's filter and the air's band
  [
    [0.031, 260, padTone.frequency],
    [0.023, 900, airTone.frequency]
  ].forEach(([frequency, depth, param]) => {
    const osc = ctx.createOscillator();
    osc.frequency.value = frequency;
    const amount = ctx.createGain();
    amount.gain.value = depth;
    osc.connect(amount).connect(param);
    osc.start();
  });

  // A chord fades in over a couple of seconds while the previous one fades out
  let voice = null;
  const playChord = index => {
    const chord = CHORDS[index];
    const at = now();
    const pad = ctx.createGain();
    pad.gain.value = 0;
    pad.connect(padTone);
    const low = ctx.createGain();
    low.gain.value = 0;
    low.connect(dry);
    const sources = [];
    const begin = osc => {
      osc.start(at);
      sources.push(osc);
    };
    chord.pad.forEach((note, i) => {
      const level = ctx.createGain();
      level.gain.value = 0.03;
      const pan = ctx.createStereoPanner();
      pan.pan.value = PANS[i];
      level.connect(pan).connect(pad);
      [-6, 6].forEach(cents => {
        const osc = ctx.createOscillator();
        osc.setPeriodicWave(wave);
        osc.frequency.value = midi(note);
        osc.detune.value = cents + (Math.random() - 0.5) * 4;
        osc.connect(level);
        begin(osc);
      });
      // Each note breathes at its own slow rate
      const breath = ctx.createOscillator();
      breath.frequency.value = 0.04 + i * 0.017;
      const depth = ctx.createGain();
      depth.gain.value = 0.011;
      breath.connect(depth).connect(level.gain);
      begin(breath);
    });
    const sub = ctx.createOscillator();
    sub.frequency.value = midi(chord.root);
    const subLevel = ctx.createGain();
    subLevel.gain.value = 0.045;
    sub.connect(subLevel).connect(low);
    begin(sub);
    glide(pad.gain, 1, 1.1, at);
    glide(low.gain, 1, 1.4, at);
    if (voice) {
      const old = voice;
      glide(old.pad.gain, 0, 1.3, at);
      glide(old.low.gain, 0, 1.1, at);
      old.sources.forEach(osc => osc.stop(at + 7));
      setTimeout(() => {
        old.pad.disconnect();
        old.low.disconnect();
      }, 7500);
    }
    voice = { pad, low, sources };
  };

  // A soft FM glass note: a sine whose sine modulator dies away
  const bell = (note, { level = 0.03, decay = 3, pan = 0, ratio = 2, at = now() } = {}) => {
    const f = midi(note);
    const carrier = ctx.createOscillator();
    carrier.frequency.value = f;
    const mod = ctx.createOscillator();
    mod.frequency.value = f * ratio;
    const depth = ctx.createGain();
    depth.gain.setValueAtTime(f * 1.4, at);
    depth.gain.exponentialRampToValueAtTime(f * 0.05, at + decay * 0.45);
    mod.connect(depth).connect(carrier.frequency);
    const amp = ctx.createGain();
    amp.gain.setValueAtTime(0.0001, at);
    amp.gain.exponentialRampToValueAtTime(level, at + 0.008);
    amp.gain.exponentialRampToValueAtTime(0.0001, at + decay);
    const panner = ctx.createStereoPanner();
    panner.pan.value = pan;
    carrier.connect(amp).connect(panner).connect(glass);
    [carrier, mod].forEach(osc => {
      osc.start(at);
      osc.stop(at + decay + 0.05);
    });
  };

  let on = false;
  let visible = true;
  let current = clampChapter(chapter);
  let target = current;
  let welcomed = false;
  let lastNote = 0;
  let lastGlint = 0;
  let speed = 0;
  let speedAt = 0;
  let chordTimer = 0;
  let glintTimer = 0;
  let sleepTimer = 0;
  const audible = () => on && visible;
  // Scroll energy (0…1), fading over half a second once the scrolling stops
  const energy = () => speed * Math.exp(-(performance.now() - speedAt) / 500);

  // Glass notes at random, from the chord that's playing; scrolling brings them closer together
  const glint = () => {
    clearTimeout(glintTimer);
    if (!audible()) return;
    const notes = sparkle(CHORDS[current]);
    let note = notes[Math.floor(Math.random() * notes.length)];
    if (note === lastNote) note = notes[(notes.indexOf(note) + 2) % notes.length];
    lastNote = note;
    lastGlint = performance.now();
    bell(note, { level: 0.09 + Math.random() * 0.06, decay: 2.4 + Math.random() * 2.2, pan: (Math.random() * 2 - 1) * 0.7 });
    glintTimer = setTimeout(glint, (1800 + Math.random() * 2600) * (1 - 0.7 * energy()));
  };

  const apply = () => {
    clearTimeout(sleepTimer);
    clearTimeout(glintTimer);
    if (audible()) {
      quietly(ctx.resume());
      glide(master.gain, LEVEL, 0.9);
      glintTimer = setTimeout(glint, 900);
    } else {
      glide(master.gain, 0, 0.3);
      sleepTimer = setTimeout(() => !audible() && ctx.state === 'running' && quietly(ctx.suspend()), 1500);
    }
  };

  playChord(current);

  return {
    get on() {
      return on;
    },
    start() {
      on = true;
      apply();
      if (welcomed) return;
      welcomed = true;
      const { root } = CHORDS[current];
      bell(root + 36, { level: 0.13, decay: 4, pan: -0.3, at: now() + 0.5 });
      bell(root + 43, { level: 0.1, decay: 4.5, pan: 0.35, at: now() + 0.85 });
    },
    stop() {
      on = false;
      apply();
    },
    // Silent while the stage is off screen or the tab is hidden
    setVisible(next) {
      if (next === visible) return;
      visible = next;
      apply();
    },
    // The chord changes once the scroll settles on a chapter, so a fast scroll doesn't strum
    // through every one, and a note from the new chord marks the arrival
    setChapter(index) {
      target = clampChapter(index);
      clearTimeout(chordTimer);
      chordTimer = setTimeout(() => {
        if (target === current) return;
        current = target;
        playChord(current);
        if (audible()) bell(CHORDS[current].pad[4] + 12, { level: 0.12, decay: 3.6, pan: (Math.random() * 2 - 1) * 0.5 });
      }, 350);
    },
    // Scroll speed in chapters per second: the pad brightens, the air swells, glass notes gather
    setMotion(chaptersPerSecond) {
      speed = Math.min(1, chaptersPerSecond / 1.6);
      speedAt = performance.now();
      if (!audible()) return;
      const at = now();
      glide(padTone.frequency, PAD_CUTOFF + PAD_OPEN * speed, 0.2, at);
      padTone.frequency.setTargetAtTime(PAD_CUTOFF, at + 0.35, 0.9);
      glide(airLevel.gain, AIR + 0.06 * speed, 0.2, at);
      airLevel.gain.setTargetAtTime(AIR, at + 0.35, 1.1);
      if (speed > 0.4 && performance.now() - lastGlint > 700) glint();
    },
    // Hovering a world: its chord's root, high and glassy
    hover(index) {
      if (!audible()) return;
      const k = clampChapter(index);
      bell(CHORDS[k].root + 36, { level: 0.18, decay: 1.8, ratio: 3, pan: (k - 2.5) * 0.22 });
    },
    // The dive: a rising sweep, a low boom and the destination's chord; a falling sweep and a
    // soft thump on the way out
    whoosh(rising = true) {
      if (!audible()) return;
      const at = now();
      const source = ctx.createBufferSource();
      source.buffer = sweepNoise;
      const band = ctx.createBiquadFilter();
      band.type = 'bandpass';
      band.Q.value = 1.1;
      band.frequency.setValueAtTime(rising ? 240 : 3200, at);
      band.frequency.exponentialRampToValueAtTime(rising ? 3600 : 200, at + 1.1);
      const swell = ctx.createGain();
      swell.gain.setValueAtTime(0.0001, at);
      swell.gain.exponentialRampToValueAtTime(0.18, at + 0.45);
      swell.gain.exponentialRampToValueAtTime(0.0001, at + 1.4);
      source.connect(band).connect(swell);
      swell.connect(dry);
      swell.connect(reverb);
      source.start(at);
      source.stop(at + 1.5);

      const boom = ctx.createOscillator();
      boom.frequency.setValueAtTime(rising ? 84 : 62, at + 0.12);
      boom.frequency.exponentialRampToValueAtTime(rising ? 36 : 40, at + 1);
      const thump = ctx.createGain();
      thump.gain.setValueAtTime(0.0001, at + 0.12);
      thump.gain.exponentialRampToValueAtTime(rising ? 0.32 : 0.16, at + 0.17);
      thump.gain.exponentialRampToValueAtTime(0.0001, at + 1.5);
      boom.connect(thump).connect(dry);
      boom.start(at + 0.12);
      boom.stop(at + 1.6);

      if (rising) CHORDS[target].pad.slice(-3).forEach((note, i) => bell(note + 12, { level: 0.12, decay: 3.2, pan: (i - 1) * 0.5, at: at + 0.35 + i * 0.09 }));
    },
    destroy() {
      on = false;
      [chordTimer, glintTimer, sleepTimer].forEach(clearTimeout);
      quietly(ctx.close());
    }
  };
};
